import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { Program } from './types';

export type RaidType = 'RISK' | 'ASSUMPTION' | 'ISSUE' | 'DECISION' | 'DEPENDENCY';
export type RaidStatus = 'OPEN' | 'MITIGATING' | 'CLOSED' | 'ACCEPTED';
export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type Party = 'CONSULTANT' | 'CLIENT' | 'VENDOR' | 'OTHER';
export type Band = 'LOW' | 'MEDIUM' | 'HIGH';

export interface RaidItem {
  id: string;
  ref: string;
  type: RaidType;
  seq: number;
  title: string;
  description: string | null;
  status: RaidStatus;
  phaseId: string | null;
  ownerParty: Party;
  ownerId: string | null;
  ownerName: string | null;
  raisedBy: string | null;
  raisedAt: string;
  targetDate: string | null;
  sourceRef: string | null;
  likelihood: number | null;
  impact: number | null;
  score: number | null;
  band: Band | null;
  mitigation: string | null;
  severity: Severity | null;
  resolution: string | null;
  options: Array<{ label: string; notes?: string }> | null;
  choice: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  rationale: string | null;
  taskIds: string[];
  deliverableIds: string[];
  comments: number;
  canEdit: 'all' | string[];
  canDecide: boolean;
}

export interface RaidDetail extends RaidItem {
  commentList: Array<{ id: string; body: string; createdAt: string; author: { id: string; name: string } }>;
  history: Array<{
    at: string;
    action: string;
    actor: string;
    changes: Array<{ field: string; from: unknown; to: unknown }>;
  }>;
}

export const RAID_TYPES: RaidType[] = ['RISK', 'ASSUMPTION', 'ISSUE', 'DECISION', 'DEPENDENCY'];
export const TYPE_LABEL: Record<RaidType, string> = {
  RISK: 'Risk',
  ASSUMPTION: 'Assumption',
  ISSUE: 'Issue',
  DECISION: 'Decision',
  DEPENDENCY: 'Dependency',
};
export const TYPE_PLURAL: Record<RaidType, string> = {
  RISK: 'Risks',
  ASSUMPTION: 'Assumptions',
  ISSUE: 'Issues',
  DECISION: 'Decisions',
  DEPENDENCY: 'Dependencies',
};
export const STATUS_LABEL: Record<RaidStatus, string> = {
  OPEN: 'Open',
  MITIGATING: 'Mitigating',
  CLOSED: 'Closed',
  ACCEPTED: 'Accepted',
};
export const SEVERITIES: Severity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const PARTIES: Party[] = ['CONSULTANT', 'CLIENT', 'VENDOR', 'OTHER'];

export const partyLabel = (p: Party, program: Pick<Program, 'clientShortName' | 'consultantName'>) =>
  ({ CONSULTANT: program.consultantName, CLIENT: program.clientShortName, VENDOR: 'Vendor', OTHER: 'Other' })[p];

export const isOpen = (i: Pick<RaidItem, 'status'>) => i.status === 'OPEN' || i.status === 'MITIGATING';

/** Score bands, a stated default: 1 to 6 low, 8 to 12 medium, 15 to 25 high. */
export const bandOf = (score: number): Band => (score >= 15 ? 'HIGH' : score >= 8 ? 'MEDIUM' : 'LOW');
export const BAND_TONE = { LOW: 'ok', MEDIUM: 'warn', HIGH: 'bad' } as const;
export const SEVERITY_TONE = { LOW: 'neutral', MEDIUM: 'warn', HIGH: 'bad', CRITICAL: 'bad' } as const;

export const raidKey = (programId: string) => ['raid', programId] as const;

export function useRaid(programId: string, f: { phaseId?: string } = {}) {
  return useQuery({
    queryKey: [...raidKey(programId), 'list', f.phaseId ?? ''],
    queryFn: () => api.get<RaidItem[]>(`/api/programs/${programId}/raid${f.phaseId ? `?phaseId=${f.phaseId}` : ''}`),
  });
}

export function useRaidItem(programId: string, id: string) {
  return useQuery({
    queryKey: [...raidKey(programId), 'one', id],
    queryFn: () => api.get<RaidDetail>(`/api/programs/${programId}/raid/${id}`),
  });
}

/** Open a RAID item in the slide-over from anywhere (?raid=<id>). */
export function useOpenRaid() {
  const [params, setParams] = useSearchParams();
  return useCallback(
    (id: string | null) => {
      const next = new URLSearchParams(params);
      if (id) next.set('raid', id);
      else next.delete('raid');
      setParams(next, { replace: false });
    },
    [params, setParams],
  );
}
